import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import { useState } from "react";
import { toast } from "react-toastify";

import EquipmentMasterCatalogApi from "../../../../../infrastructure/api/equipmentMasterCatalog";

const CounterGrid = ({ counters }) => {
  if (!counters) return null;
  const entries = [
    ["دسته ایجاد", counters.createdCategories],
    ["دسته به‌روز", counters.updatedCategories],
    ["برند ایجاد", counters.createdBrands],
    ["تولیدکننده ایجاد", counters.createdManufacturers],
    ["تعریف مشخصه ایجاد", counters.createdSpecDefinitions],
    ["تجهیز ایجاد", counters.createdEquipmentItems],
    ["تجهیز موجود", counters.matchedExistingEquipmentItems],
    ["مقدار مشخصه", counters.createdSpecValues],
    ["سند", counters.createdDocuments],
    ["نام مستعار", counters.createdAliases],
    ["ردیف ردشده", counters.rowsSkipped],
  ];

  return (
    <Grid container spacing={1} sx={{ mt: 1 }}>
      {entries.map(([label, value]) => (
        <Grid item xs={6} md={4} key={label}>
          <Typography variant="body2">
            {label}: <strong>{value ?? 0}</strong>
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
};

const EquipmentSeedImportDialog = ({ open, onClose, onCommitted }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);

  const reset = () => {
    setFile(null);
    setDryRunResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const runDryRun = async () => {
    if (!file) {
      toast.error("فایل اکسل را انتخاب کنید");
      return;
    }
    setLoading(true);
    try {
      const res = await EquipmentMasterCatalogApi.importSeedExcel(file, false);
      if (res.ok) {
        setDryRunResult(res.data);
        toast.success("ارزیابی آزمایشی انجام شد");
      } else {
        toast.error(res.message || "خطا در ارزیابی آزمایشی");
      }
    } finally {
      setLoading(false);
    }
  };

  const runCommit = async () => {
    if (!file) {
      toast.error("فایل اکسل را انتخاب کنید");
      return;
    }
    if (!dryRunResult?.success) {
      toast.error("ابتدا ارزیابی آزمایشی بدون خطا انجام دهید");
      return;
    }
    setLoading(true);
    try {
      const res = await EquipmentMasterCatalogApi.importSeedExcel(file, true);
      if (res.ok && res.data?.success) {
        toast.success(res.data?.message || "ایمپورت ذخیره شد");
        onCommitted?.();
        handleClose();
      } else {
        toast.error(res.message || res.data?.message || "خطا در ذخیره ایمپورت");
        if (res.data) setDryRunResult(res.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!dryRunResult) return;
    const blob = new Blob([JSON.stringify(dryRunResult, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eqm-seed-import-report-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>ایمپورت Seed اکسل کاتالوگ EQM</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          فایل <strong>solarent_eqm_iran_market_seed.xlsx</strong> را بارگذاری کنید.
          ابتدا ارزیابی آزمایشی (بدون ذخیره) و سپس در صورت تأیید، ذخیره نهایی.
          تجهیزات به‌صورت Draft/PendingReview وارد می‌شوند — PublicCode صادر نمی‌شود.
        </Typography>

        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
          انتخاب فایل اکسل
          <input
            type="file"
            hidden
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setDryRunResult(null);
            }}
          />
        </Button>
        {file ? (
          <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
            {file.name}
          </Typography>
        ) : null}

        {dryRunResult ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity={dryRunResult.success ? "success" : "warning"} sx={{ mb: 1 }}>
              {dryRunResult.message}
            </Alert>
            <Typography variant="subtitle2">خلاصه شیت‌ها</Typography>
            <Typography variant="body2">
              Categories: {dryRunResult.sheetCounts?.categories ?? 0} | Brands:{" "}
              {dryRunResult.sheetCounts?.brands ?? 0} | Items:{" "}
              {dryRunResult.sheetCounts?.equipmentItems ?? 0} | Aliases:{" "}
              {dryRunResult.sheetCounts?.aliases ?? 0}
            </Typography>
            <CounterGrid counters={dryRunResult.counters} />
            {dryRunResult.errors?.length > 0 ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="error">
                  خطاها
                </Typography>
                <List dense>
                  {dryRunResult.errors.slice(0, 10).map((e, i) => (
                    <ListItem key={i} disablePadding>
                      <ListItemText primary={e} />
                    </ListItem>
                  ))}
                </List>
              </>
            ) : null}
            {dryRunResult.warnings?.length > 0 ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="warning.main">
                  هشدارها
                </Typography>
                <List dense>
                  {dryRunResult.warnings.slice(0, 10).map((w, i) => (
                    <ListItem key={i} disablePadding>
                      <ListItemText primary={w} />
                    </ListItem>
                  ))}
                </List>
              </>
            ) : null}
            {dryRunResult.reportFilePath ? (
              <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                گزارش سرور: {dryRunResult.reportFilePath}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>بستن</Button>
        {dryRunResult ? (
          <Button onClick={downloadReport}>دانلود گزارش JSON</Button>
        ) : null}
        <Button
          onClick={runDryRun}
          disabled={loading || !file}
          startIcon={<PlayArrowIcon />}
        >
          ارزیابی آزمایشی
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={runCommit}
          disabled={loading || !file || !dryRunResult?.success}
          startIcon={<SaveIcon />}
        >
          ذخیره ایمپورت
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipmentSeedImportDialog;
